import React, { useState } from "react";
import {
  Card,
  Table,
  Input,
  Select,
  Button,
  Space,
  Dropdown,
  Empty,
} from "antd";
import { EllipsisOutlined } from "@ant-design/icons";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import theme from "../../themes";
import { ReactComponent as SearchIcon } from "../../assets/icons/search.svg";
import { useNavigate } from "react-router-dom";
import type { ColumnsType } from "antd/es/table";
import { useAppDispatch, useAppSelector } from "../../store";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { getCampaignReportThunk } from "../../store/actions/campaign.action";
import { DownloadOutlined } from "@ant-design/icons";
import { ReactComponent as ArrowUpIcon } from "../../assets/icons/arrowUp.svg";
import { ReactComponent as ArrowDownIcon } from "../../assets/icons/arrowDown.svg";
import { generateDynamicColumns } from "./utils";
import { getCallStatusLabel } from "../../constants/callStatus";
import { getReportThunk } from "../../store/actions/report.action";

dayjs.extend(utc);

const { Option } = Select;

const ReportPage: React.FC = () => {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 5;
  const [campaignFilter, setCampaignFilter] = useState("outbound");
  const [sorter, setSorter] = useState<{
    field: "createdAt";
    order: "ascend" | "descend";
  }>({ field: "createdAt", order: "descend" });
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { loading } = useAppSelector((state) => state.report);
  const [reports, setReports] = useState({
    data: [],
    pagination: {
      totalItems: 0,
      totalPages: 0,
    },
  });

  const handleCustomSort = (field: "createdAt") => {
    let nextOrder: "ascend" | "descend" = "descend";
    if (sorter.field === field) {
      nextOrder = sorter.order === "ascend" ? "descend" : "ascend";
    }
    setSorter({ field, order: nextOrder });
  };

  const fetchReports = async (page: number) => {
    const res = await dispatch(
      getReportThunk({
        page,
        limit: pageSize,
        keyword: search,
        status: "",
        direction_filter: campaignFilter,
        sort: {
          [sorter.field]: sorter.order === "descend" ? -1 : 1,
        },
      })
    ).unwrap();
    const { data, pagination } = res.data || { data: [], pagination: {} };
    const dataWithKeys = (data || []).map((item: any, index: number) => ({
      ...item,
      key: item.key || item._id || item.id || `row-${index}`,
    }));
    setReports({
      data: dataWithKeys,
      pagination: pagination || { totalItems: 0, totalPages: 0 },
    });
  };

  // CSV Download Handler
  const handleDownloadCSV = async (
    campaignId: string,
    campaignName: string
  ) => {
    try {
      const res = await dispatch(
        getCampaignReportThunk({ campaignId, agentId: "" })
      ).unwrap();
      const campaignReport = res.data;
      if (!campaignReport.length) return;

      // Generate dynamic columns for CSV headers
      const columns: any[] = generateDynamicColumns(campaignReport, t) as any[];

      const getSummaryStatusLabel = (rawStatus: any) => {
        if (!rawStatus) return "";
        const key = String(rawStatus);
        const knownKeys = ["achieved", "partially_achieved", "not_achieved"];
        const lookupKey = knownKeys.includes(key) ? key : "unknown";
        const translated = t(`reports.summaryStatus.${lookupKey}`);
        if (
          typeof translated === "string" &&
          translated !== `reports.summaryStatus.${lookupKey}`
        ) {
          return translated;
        }
        return key;
      };

      const tableHeaders = columns.map((col) => {
        if (col.title?.props?.children?.[0]) {
          return col.title.props.children[0];
        }
        if (typeof col.title === "string") {
          return col.title;
        }
        return col.title;
      });
      const hasUnsubscribed = campaignReport.some(
        (row: any) =>
          String(row.call_analysis?.unsubscribe || "")
            .trim()
            .toUpperCase() === "YES"
      );
      const headers = [
        ...tableHeaders,
        ...(hasUnsubscribed ? ["Unsubscribe"] : []),
        t("reports.tableColumns.goalAchievedStatus") || "Goal achieved status",
      ];

      const rows = campaignReport.map((row: any) => {
        const tableRow = columns.map((col) => {
          const goalRawStatus = row.call_analysis?.goal_achieved;
          const goalStatusLabel = goalRawStatus
            ? getSummaryStatusLabel(goalRawStatus)
            : "";

          if (col.dataIndex === "recipient_name")
            return row.recipient_name || "-";
          if (col.dataIndex === "phone")
            return campaignFilter === "outbound" ? row.to : row.from || "-";
          if (col.dataIndex === "duration") return row.duration || "-";
          if (col.dataIndex === "status") return getCallStatusLabel(row.status);
          if (col.dataIndex === "start_time") {
            return row.start_time
              ? dayjs(row.start_time).format("hh:mm A, MMM DD, YYYY")
              : "-";
          }
          if (col.key === "agent_goal") {
            return goalStatusLabel || "-";
          }
          if (col.key === "goal_result_icon") {
            const key = String(goalRawStatus || "unknown");
            if (key === "achieved")
              return t("reports.goalResult.success") || "Success";
            if (key === "partially_achieved")
              return t("reports.goalResult.partial") || "Partial";
            if (key === "not_achieved")
              return t("reports.goalResult.failed") || "Failed";
            return t("reports.goalResult.unknown") || "Unknown";
          }
          if (col.key === "summary") {
            const callAnalysis = row.call_analysis;
            const followUp = callAnalysis?.follow_up;
            const summary = callAnalysis?.summary;
            const reason = callAnalysis?.reason;
            const recommendation =
              typeof followUp === "string"
                ? followUp
                : followUp?.recommendation;

            if (summary || reason || recommendation) {
              const parts = [];
              if (summary) parts.push(`Summary: ${summary}`);
              if (reason) parts.push(`Reason: ${reason}`);
              if (recommendation) parts.push(`Follow up: ${recommendation}`);
              if (parts.length > 0) return parts.join(" | ");
            }
            return row.summary || "-";
          }

          // Handle dynamic action columns
          if (Array.isArray(col.dataIndex) && col.dataIndex[0] === "action") {
            const actionKey = col.dataIndex[1];
            const actionData = row.action?.[actionKey];

            if (!actionData || actionData.value == null) {
              return "-";
            }

            const items = Array.isArray(actionData.value)
              ? actionData.value
              : [actionData.value];

            if (items.length === 0) return "-";

            const formatTimeSlot = (slot: any) => {
              const { start_time, end_time } = slot || {};
              if (!start_time && !end_time) return "-";
              try {
                const startTime = start_time ? dayjs.utc(start_time) : null;
                const endTime = end_time ? dayjs.utc(end_time) : null;

                if (!startTime && endTime)
                  return `${endTime.format("h:mm A")}, ${endTime.format(
                    "MMM DD, YYYY"
                  )}`;
                if (startTime && !endTime)
                  return `${startTime.format("h:mm A")}, ${startTime.format(
                    "MMM DD, YYYY"
                  )}`;

                if (startTime?.isValid() && endTime?.isValid()) {
                  if (startTime.isSame(endTime, "day")) {
                    return `${startTime.format("h:mm A")} - ${endTime.format(
                      "h:mm A"
                    )}, ${startTime.format("MMM DD, YYYY")}`;
                  } else {
                    return `${startTime.format("h:mm A")}, ${startTime.format(
                      "MMM DD, YYYY"
                    )} - ${endTime.format("h:mm A")}, ${endTime.format(
                      "MMM DD, YYYY"
                    )}`;
                  }
                }
                return (
                  `${start_time || ""} - ${end_time || ""}`.replace(
                    /^- | -$/,
                    ""
                  ) || "-"
                );
              } catch (e) {
                return (
                  `${start_time || ""} - ${end_time || ""}`.replace(
                    /^- | -$/,
                    ""
                  ) || "-"
                );
              }
            };

            const normalizeValue = (val: any) => {
              if (typeof val === "string") {
                if (/[<>]/.test(val)) {
                  return val.replace(/<[^>]*>/g, "").trim() || val;
                }
                return val;
              }
              return JSON.stringify(val);
            };

            const toLabel = (key: string) =>
              key.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());

            const serializeItem = (item: any) => {
              if (item && typeof item === "object") {
                const hasTime = Boolean(item.start_time || item.end_time);
                const parts: string[] = [];
                if (hasTime) {
                  const label =
                    item.start_time && item.end_time
                      ? `${toLabel("start_time")} - ${toLabel("end_time")}`
                      : item.start_time
                      ? toLabel("start_time")
                      : toLabel("end_time");
                  parts.push(`${label}: ${formatTimeSlot(item)}`);
                }
                Object.keys(item)
                  .filter((k) => k !== "start_time" && k !== "end_time")
                  .forEach((k) => {
                    parts.push(`${toLabel(k)}: ${normalizeValue(item[k])}`);
                  });
                return parts.join(" | ");
              }
              return normalizeValue(item);
            };

            return items.map(serializeItem).join(";\n");
          }

          return "-";
        });
        const isUnsubscribed =
          String(row.call_analysis?.unsubscribe || "").trim().toUpperCase() ===
          "YES";
        return [
          ...tableRow,
          ...(hasUnsubscribed ? [isUnsubscribed ? "YES" : "-"] : []),
          getSummaryStatusLabel(row.call_analysis?.goal_achieved),
        ];
      });

      const csvContent = [headers, ...rows]
        .map((e) =>
          e
            .map((field: any) =>
              typeof field === "string" && field.includes(",")
                ? `"${field.replace(/"/g, '""')}"`
                : field
            )
            .join(",")
        )
        .join("\n");

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${campaignName || "campaign_report"}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      // Optionally show error message
      console.error("Failed to download CSV", error);
    }
  };

  const columns: ColumnsType<any> = [
    ...(campaignFilter === "outbound"
      ? [
          {
            title: t("reports.campaignName"),
            dataIndex: "campaign_name",
            key: "campaign_name",
            width: "45%",
            render: (text: string, record: any) => (
              <button
                type="button"
                onClick={() =>
                  navigate(`/reports/${record._id}`, {
                    state: { campaignName: record.campaign_name },
                  })
                }
                style={{
                  color: "#1890ff",
                  fontWeight: 500,
                  cursor: "pointer",
                  background: "none",
                  border: "none",
                  padding: 0,
                }}
              >
                {text}
              </button>
            ),
          },
        ]
      : []),
    ...(campaignFilter === "inbound"
      ? [
          {
            title: t("reports.agentName"),
            dataIndex: "agent_name",
            key: "agent_name",
            width: "45%",
            render: (text: string, record: any) => (
              <button
                type="button"
                onClick={() =>
                  navigate(`/reports/${record._id}`, {
                    state: {
                      agentName: record.agent_name,
                      isInbound: true,
                    },
                  })
                }
                style={{
                  color: "#1890ff",
                  fontWeight: 500,
                  cursor: "pointer",
                  background: "none",
                  border: "none",
                  padding: 0,
                }}
              >
                {text}
              </button>
            ),
          },
        ]
      : []),
    {
      title: (
        <span
          style={{
            cursor: "pointer",
            color: sorter.field === "createdAt" ? "#000" : "inherit",
          }}
          onClick={() => handleCustomSort("createdAt")}
        >
          {t("reports.createdTime")}{" "}
          {sorter.field === "createdAt" &&
            (sorter.order === "ascend" ? (
              <ArrowUpIcon style={{ marginLeft: 4, verticalAlign: "middle" }} />
            ) : (
              <ArrowDownIcon
                style={{ marginLeft: 4, verticalAlign: "middle" }}
              />
            ))}
        </span>
      ),
      dataIndex: "createdAt",
      key: "createdAt",
      width: "45%",
      render: (createdAt: string) =>
        createdAt ? dayjs(createdAt).format("hh:mm A, MMM DD, YYYY") : "-",
    },
    {
      title: "",
      key: "action",
      width: "10%",
      align: "right",
      render: (_: any, record: any) => (
        <Dropdown
          menu={{
            items: [
              {
                key: "download",
                label: (
                  <span
                    onClick={() =>
                      handleDownloadCSV(record._id, record.campaign_name)
                    }
                  >
                    <DownloadOutlined style={{ marginRight: 8 }} />
                    {t("reports.downloadCsv")}
                  </span>
                ),
              },
            ],
          }}
          trigger={["click"]}
          placement="bottomRight"
        >
          <Button type="text" icon={<EllipsisOutlined />} />
        </Dropdown>
      ),
    },
  ];

  // Reset to page 1 when search or filter changes
  React.useEffect(() => {
    setCurrentPage(1);
  }, [search, campaignFilter, sorter]);

  React.useEffect(() => {
    fetchReports(currentPage);
  }, [currentPage, search, campaignFilter, sorter]);

  return (
    <MainContent>
      <Title>{t("reports.title")}</Title>
      <CardWrapper>
        <Space
          style={{
            marginBottom: 16,
            width: "100%",
            justifyContent: "space-between",
            display: "flex",
          }}
        >
          <Space>
            <SortSelect
              value={campaignFilter}
              style={{ minWidth: 200 }}
              size="large"
              onChange={(value) => setCampaignFilter(value as string)}
            >
              <Option value="outbound">{t("reports.outboundCampaigns")}</Option>
              <Option value="inbound">{t("reports.inboundCalls")}</Option>
            </SortSelect>
          </Space>
          <SearchWrapper>
            <SearchIcon />
            <CustomSearch
              placeholder={
                campaignFilter === "outbound"
                  ? t("reports.searchCampaign")
                  : t("reports.searchAgent")
              }
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </SearchWrapper>
        </Space>
        <StyledTable
          dataSource={reports.data}
          columns={columns}
          loading={loading}
          pagination={{
            pageSize: 5,
            position: ["bottomCenter"],
            current: currentPage,
            total: reports.pagination.totalItems,
            onChange: setCurrentPage,
            showSizeChanger: false,
          }}
          rowKey="key"
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  <EmptyStateTitle>{t("common.noData")}</EmptyStateTitle>
                }
              />
            ),
          }}
        />
      </CardWrapper>
    </MainContent>
  );
};

export default ReportPage;

// Styled Components
const MainContent = styled.div`
  flex: 1;
  padding: 52px 72px;
  gap: 32px;
  display: flex;
  flex-direction: column;
  background-color: ${theme.colors.grey3};
  min-height: 100vh;

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: 600;
  height: 40px;
  display: flex;
  align-items: center;
`;

const CardWrapper = styled(Card)`
  border-radius: 12px;
  border: 1px solid ${theme.colors.grey2};
`;

const SearchWrapper = styled.div`
  width: 240px;
  display: flex;
  background-color: ${theme.colors.grey4};
  border: 1px solid ${theme.colors.grey2};
  border-radius: 8px;
  padding: 0px 8px;
  align-items: center;
  box-sizing: border-box;
  gap: 8px;
`;

const CustomSearch = styled(Input)`
  border-radius: 12px;
  height: 40px;
  font-size: 16px;
  border: none;
  box-shadow: none;
  background-color: ${theme.colors.grey4};
  padding: 0;

  &:hover,
  &:focus {
    border: none;
    box-shadow: none;
    background-color: ${theme.colors.grey4};
  }
`;

const SortSelect = styled(Select)`
  .ant-select-selector {
    background: ${theme.colors.grey4} !important;
    border: 1px solid ${theme.colors.grey2} !important;
    box-shadow: none !important;
    outline: none !important;
  }
  .ant-select-selector:focus,
  .ant-select-selector:active {
    box-shadow: none !important;
    outline: none !important;
  }
`;

const EmptyStateTitle = styled.div`
  font-size: 16px;
  font-weight: 500;
  margin-bottom: 8px;
`;

const StyledTable = styled(Table)`
  .ant-table-thead > tr > th {
    background-color: white;
    border-bottom: none;
    padding: 10px 16px;
    font-size: 14px;
    line-height: 20px;
    color: ${theme.colors.grey1};
    font-weight: 500;
  }

  .ant-table-thead > tr > th::before {
    display: none;
  }

  .ant-table-tbody > tr > td {
    padding: 16px;
    font-size: 14px;
    line-height: 20px;
    font-weight: 500;
    border-bottom: none;
  }

  .ant-table-tbody > tr:nth-child(odd) {
    background-color: ${theme.colors.grey3};
  }

  .ant-table-tbody > tr:nth-child(even) {
    background-color: ${theme.colors.grey4};
  }

  .ant-table table {
    border-spacing: 0 4px;
    border-collapse: separate;
  }

  .ant-table-tbody > tr > td:last-child {
    border-top-right-radius: 16px;
    border-bottom-right-radius: 16px;
  }
`;
